'use strict';

class SceneMenuController {
    constructor() {
        this.availableListMenuController = null;
        this.availableStateList = [];

        this.backupModelList = null;
        this.backupImageList = null;
        this.backupVideoList = null;
        this.backupDocumentList = null;
    }

    updateItemsRecursivelyById(newItems, existingItems) {
        const flattenItems = (items) => {
            let result = [];
            items.forEach(item => {
                if (!item.isFolder) {
                    result.push(item);
                } else if (item.children) {
                    result.push(...flattenItems(item.children));
                }
            });
            return result;
        };

        for (const newItem of newItems) {
            const index = newItems.indexOf(newItem);
            if (!newItem.isFolder) {
                const existingItem = getItemRecursivelyByID(existingItems, newItem.id);
                if (existingItem) {
                    newItems[index] = existingItem;
                }
            } else if (newItem.children && newItem.children.length > 0) {
                const flatExistingItems = flattenItems(existingItems);
                this.updateItemsRecursivelyById(newItem.children, flatExistingItems);
            }
        }
    }

    getAvailableResourceIds(itemsArray) {
        const getIdsRecursively = (items) => {
            let ids = [];
            if (items && Array.isArray(items)) {
                items.forEach(item => {
                    if (!item.isFolder) {
                        ids.push(item.id);
                    } else if (item.children && item.children.length > 0) {
                        ids.push(...getIdsRecursively(item.children));
                    }
                });
            }
            return ids;
        };

        return getIdsRecursively(itemsArray || []);
    }

    findItemById(itemsArray, id) {
        if (!itemsArray || !Array.isArray(itemsArray)) return null;
        for (const item of itemsArray) {
            if (!item.isFolder && item.id === id) return item;
            if (item.children?.length) {
                const found = this.findItemById(item.children, id);
                if (found) return found;
            }
        }
        return null;
    }

    getResourceListData(resourceType = "", cores = {}) {
        const training = TrainingTreeController._trainingFeature.getTrainingData();
        const folderMap = new Map();
        const rootFolders = [];

        training.directories.forEach(directory => {
            if (directory.type === resourceType) {
                directory.isFolder = true;
                directory.children = [];
                folderMap.set(directory.id, directory);
            }
        });

        folderMap.forEach(folder => {
            if (folder.parent) {
                const parentFolder = folderMap.get(folder.parent);
                if (parentFolder) {
                    parentFolder.children.push(folder);
                } else {
                    rootFolders.push(folder);
                }
            } else {
                rootFolders.push(folder);
            }
        });

        let isMedia = false, typeToCheck = "";
        if (resourceType.startsWith('MediaScreen.')) {
            typeToCheck = resourceType.replace('MediaScreen.', '');
            resourceType = 'MediaScreen';
            isMedia = true;
        }

        const uuidToResourceIdMap = {};
        for (let resource of training.resources) {
            if (resource.type === resourceType) {
                if (isMedia && resource.mediaType !== typeToCheck) {
                    continue;
                }

                if (resource.type === 'Viewpoint' && !resource.resourceId) {
                    uuidToResourceIdMap[resource.id] = resource.id;
                } else {
                    resource.isFolder = false;
                    uuidToResourceIdMap[resource.id] = resource.resourceId;
                    resource.id = resource.resourceId;
                    resource._id = resource.resourceId;
                    if (cores.hasOwnProperty(resource.resourceId)) {
                        resource.core = cores[resource.resourceId];
                    }
                }

                const parentFolder = folderMap.get(resource.parent);
                if (parentFolder) {
                    parentFolder.children.push(resource);
                } else {
                    rootFolders.push(resource);
                }
            }
        }

        /*
         * it is required to update the resource id in resourceStates
         * because the resource.id is changed to resource.resourceId
         * to match previous behavior or menu items
         */
        for (const uuid in uuidToResourceIdMap) {
            const resourceId = uuidToResourceIdMap[uuid];
            for (const state of training.states) {
                for (const resourceState of state.resourceStates) {
                    if (resourceState.id === uuid) {
                        resourceState.id = resourceId;
                    }
                }
            }
        }

        const sortedRootFolders = rootFolders.filter(f => f.isFolder);
        const rootFiles = rootFolders.filter(f => !f.isFolder);
        return [...sortedRootFolders, ...rootFiles];
    }

    getStateListData() {
        const training = TrainingTreeController._trainingFeature.getTrainingData();
        const folderMap = new Map();
        const rootFolders = [];

        training.directories.forEach(directory => {
            if (directory.type === 'State') {
                directory.isFolder = true;
                directory.children = [];
                folderMap.set(directory.id, directory);
            }
        });

        folderMap.forEach(folder => {
            if (folder.parent) {
                const parentFolder = folderMap.get(folder.parent);
                if (parentFolder) {
                    parentFolder.children.push(folder);
                } else {
                    rootFolders.push(folder);
                }
            } else {
                rootFolders.push(folder);
            }
        });

        training.states.forEach(state => {
            state.isFolder = false;
            const parentFolder = folderMap.get(state.parent);
            if (parentFolder) {
                parentFolder.children.push(state);
            } else {
                rootFolders.push(state);
            }
        });

        const sortedRootFolders = rootFolders.filter(f => f.isFolder);
        const rootFiles = rootFolders.filter(f => !f.isFolder);
        return [...sortedRootFolders, ...rootFiles];
    }

    createNewTrainingState() {
        const training = TrainingTreeController._trainingFeature.getTrainingData();
        const timeStamp = getTimeStamp();
        const keyboardCallbacks = {};
        wv.communicator.socket.communicateWithContainer({
            message: "workspaceSceneCreationStarted",
        });
        const self = this;
        keyboardCallbacks.enterCallback = function (stateName) {
            mController.loadAndResetSavedMenuState();
            if (!stateName.trim().length) {
                const vrText = "State name can not be empty";
                VRMessageController.addMessage({text: vrText, priority: VrMessagePriorityEnum.LOWPRIORITYINFO});
                return;
            }

            if (training.states.some(state => state.name.toLowerCase() === name.toLowerCase())) {
                alert("Name already exists. Please provide a new scene name");
                return;
            }

            const sceneData = SceneStateController.saveState();
            wv.communicator.socket.communicateWithContainer({
                message: "createSceneFromWorkspace",
                name: stateName,
                sceneData: sceneData
            });
        };

        keyboardCallbacks.cancelCallback = function () {
            mController.loadAndResetSavedMenuState();
        };

        keyboardController.toggleState(keyboardController.getVRKeyboardParent(), keyboardCallbacks, "Scene_" + timeStamp);
    }

    addCreateNewStateButton(parentMenuController, prop) {
        let self = this;
        let clickMenuItem = new ClickMenuItem(CreateNewStateButton.menuItemName, "Create State", null, function () {
            self.createNewTrainingState();
        });
        clickMenuItem.isVisible = prop.isVisible;
        clickMenuItem.isPresenter = prop.isPresenter;
        clickMenuItem.isViewer = prop.isViewer;
        parentMenuController.appendMenuItem(undefined, undefined, clickMenuItem);
    }

    updateAvailableListSelection(value) {
        let menuView = getCurrentActiveMenuView(mController);
        let modelNameArray = [], folderArray = [];

        getMenuItemsFromMultiPageList(menuView.menuData, modelNameArray, folderArray);

        for (let i in modelNameArray) {
            modelNameArray[i].updateCheckbox(value);
        }

        for (let i in folderArray) {
            folderArray[i].updateCheckbox(value);
        }

        if (menuView.menuData.hasOwnProperty('updateCheckbox')) {
            menuView.menuData.updateCheckbox(value);
        }
    }

    insertStateFile(file) {
        const createNewFolderMenu = (item) => {
            // we need to check resourceId as after each model loading, the cacheFiles are refreshed
            // as we can now have same model nested under folder, we distinguish cacheFiles id with uid instead of resourceId
            item.resourceId = item.hasOwnProperty('resourceId') ? item.resourceId : item.id;
            item.id = generateUUID(); // as we have nested same folders, we need ids to be unique
            let menuData = new ToggleTypeMenuData(item.id, 1, 10, undefined, updateParentUponSelection, {
                id: item.id,
                resourceId: item.resourceId
            });
            menuData.title = item.name;
            menuData.text = item.name;
            menuData.isListTypeMenu = true;
            let menuController = new MenuController(menuData);
            menuController.setMenuControlParameter(VR.scene, iq3Camera.camera, wv.selectionSphere);
            menuData.isFolder = true;

            menuController.setMenuItemWidth(0.23);
            menuController.setMenuItemHeight(0.02);
            menuController.setCloseButtonAppear(true);
            menuController.setBackAndCloseItemWidth(0.052);
            menuController.setTextAlignType(TextAlignTypeEnum.left);

            for (const child of item.children) {
                if (child.isFolder) {
                    const newFolderData = createNewFolderMenu(child);
                    menuData.addItem(newFolderData);
                } else {
                    child.resourceId = child.hasOwnProperty('resourceId') ? child.resourceId : child.id;
                    child.id = generateUUID();
                    const newItem = new ToggleCheckTypeMenuItem(child.id, child.name, false, updateParentUponSelection, {
                        id: child.id,
                        resourceId: child.resourceId
                    });
                    menuData.addItem(newItem);
                }
            }

            return menuData;
        };

        const updateParentUponSelection = (currentSelection, userData) => {
            const menuItem = this.availableListMenuController.menuData.getMenuItemByName(userData.id);
            if (menuItem.parent && menuItem.parent.menuData instanceof ToggleTypeMenuData) {
                let selectionData = true;
                menuItem.parent.menuData.items.forEach(child => {
                    if (child instanceof ToggleTypeMenuData || child instanceof ToggleCheckTypeMenuItem) {
                        selectionData = selectionData && child.currentSelection;
                    }
                })
                menuItem.parent.menuData.updateCheckbox(selectionData);
            }
        };

        let newItemData;

        if (file.isFolder) {
            newItemData = createNewFolderMenu(file);
        } else {
            file.resourceId = file.hasOwnProperty('resourceId') ? file.resourceId : file.id;
            file.id = generateUUID(); // as we have nested same folders, we need ids to be unique
            newItemData = new ToggleCheckTypeMenuItem(file.id, file.name, false, updateParentUponSelection, {
                id: file.id,
                resourceId: file.resourceId
            });
        }

        let availableView = mController.menuDataView.getThisMenuView(LoadTrainingStateMenu.menuItemName);

        if (availableView && availableView.isMenuDrawn()) {
            mController.menuDataView.insertThisMenuItemToRunningMenuData(LoadTrainingStateMenu.menuItemName, newItemData);
            availableView.refresh();
        } else {
            let menuData = mController.menuData.getThisMenuData(LoadTrainingStateMenu.menuItemName);
            if (menuData) {
                menuData.addItem(newItemData);
            }
        }
    };

    setStateFileList(stateList) {
        let self = this;

        let availableView = mController.menuDataView.getThisMenuView(LoadTrainingStateMenu.menuItemName);
        completelyRemoveThisMenuView(mController, availableView, LoadTrainingStateMenu.menuItemName);

        this.availableStateList = [];

        stateList.forEach(file => {
            self.insertStateFile(file);
            this.availableStateList.push(file);
        });
    }

    getSelectedStatesFromMenuItems() {
        const stateMenuItemsArray = [];
        const ids = [];
        const names = [];
        getMenuItemsFromMultiPageList(this.availableListMenuController.menuData, stateMenuItemsArray);
        
        for (let i in stateMenuItemsArray) {
            const menuItem = stateMenuItemsArray[i];
            if (menuItem.currentSelection) {
                ids.push(menuItem.userData.resourceId);
                names.push(menuItem.text);
                menuItem.updateCheckbox(false);
            }
        }
        return { ids, names };
    }

    async loadTrainingStatesFromXRMEnu() {
        wv.communicator.socket.communicateWithContainer({
            message: "getTrainingData"
        });

        const selectedStateIds = this.getSelectedStatesFromMenuItems().ids;

        await sleep(500);
        const training = TrainingTreeController._trainingFeature.getTrainingData();
        await TrainingTreeController._trainingFeature.loadServerLessTrainingState(training, selectedStateIds, true, false, false);
    }

    getTrainingStateToOverwriteFromXRMEnu() {
        const { ids: selectedStateIds, names: selectedStateNames } = this.getSelectedStatesFromMenuItems();

        if (!selectedStateIds || selectedStateIds.length === 0) {
            return null;
        }

        if (selectedStateIds.length > 1) {
            VRMessageController.addMessage({
                text: "Please select only one state",
                priority: VrMessagePriorityEnum.HIGHPRIORITYINFO
            });
            return null;
        }

        return { selectedStateIds, selectedStateNames };
    }

    async overwriteTrainingStatesFromXRMEnu() {
        const selected = this.getTrainingStateToOverwriteFromXRMEnu();
        if (!selected) {
            return;
        }

        const { selectedStateIds, selectedStateNames } = selected;
        const stateName = selectedStateNames[0];
        saveCurrentMenuItemState();
        wv.promptWindow3dObject.changeText("This will overwrite the State: " + stateName +".\nDo you want to proceed?");
        wv.promptWindow3dObject.askNow(function (value) {
            if (value) {
                wv.communicator.socket.communicateWithContainer({
                    message: "overwriteScenesFromWorkspace",
                    stateId: selectedStateIds[0]
                });
            }
            mController.loadAndResetSavedMenuState();
        });
    }

    initAvailableStateListMenu() {
        const onRefreshButtonClicked = () => {
            wv.communicator.socket.communicateWithContainer({
                message: "getTrainingData"
            });
        };

        const addCommandMenuButtonsForAvailableFiles = (menuController) => {
            let menuItem = new ClickMenuItem("selectAllButton_state", "Select All", true, this.updateAvailableListSelection);
            menuController.addCommandMenuItem(menuItem);
            menuItem = new ClickMenuItem("loadButton_state", "Load", undefined, this.loadTrainingStatesFromXRMEnu.bind(this));
            menuController.addCommandMenuItem(menuItem);
            menuItem = new ClickMenuItem("overwriteButton_state", "Overwrite", undefined, this.overwriteTrainingStatesFromXRMEnu.bind(this));
            menuController.addCommandMenuItem(menuItem);
            menuItem = new ClickMenuItem("deselectAllButton_state", "Deselect All", false, this.updateAvailableListSelection);
            menuController.addCommandMenuItem(menuItem);
            menuItem = new ClickMenuItem("refreshStateFiles", "Refresh\nState Files", false, onRefreshButtonClicked);
            menuController.addCommandMenuItem(menuItem);
        };

        this.availableStateList = new MenuData('Available States', 1, 10);
        this.availableStateList.isListTypeMenu = true;

        this.availableListMenuController = new MenuController(this.availableStateList);

        this.availableListMenuController.setMenuItemWidth(0.23);
        this.availableListMenuController.setMenuItemHeight(0.02);
        this.availableListMenuController.setCloseButtonAppear(true);
        this.availableListMenuController.setBackAndCloseItemWidth(0.052);
        this.availableListMenuController.setTextAlignType(TextAlignTypeEnum.left);

        addCommandMenuButtonsForAvailableFiles(this.availableListMenuController);

        this.availableListMenuController.setMenuControlParameter(VR.scene, iq3Camera.camera, wv.selectionSphere);
    }

    addLoadTrainingStateMenu(parentMenuController, prop) {
        try {
            this.initAvailableStateListMenu();
            this.availableStateList.isVisible = prop.isVisible;
            this.availableStateList.isPresenter = prop.isPresenter;
            this.availableStateList.isViewer = prop.isViewer;
            let menuText = prop.text || LoadTrainingStateMenu.text;
            parentMenuController.appendMenuItem(LoadTrainingStateMenu.menuItemName, menuText, this.availableStateList);
        } catch (error) {
            console.error('Training Editor Error in Load State list menu. Error: ' + error);
        }
    }

    updateLoadTrainingStateMenu() {
        const stateList = this.getStateListData();
        this.setStateFileList(stateList);
    }

    async updateAvailableModelListMenu(cores = {}) {
        const modelList = this.getResourceListData("Model", cores);
        if (!this.backupModelList && wv.availableFileList && wv.availableFileList.availableFileList && wv.availableFileList.availableFileList.length > 0) {
            this.backupModelList = JSON.parse(JSON.stringify(wv.availableFileList.availableFileList));
        }

        const sourceList = this.backupModelList || (wv.availableFileList ? wv.availableFileList.availableFileList : []);
        if (sourceList && sourceList.length > 0) {
            this.updateItemsRecursivelyById(modelList, sourceList);
        }

        const models = await getModelByID({id: this.getAvailableResourceIds(modelList)});
        if (models && models.length) {
            wv.effectController.meetingInfo.data.cacheFiles.push(...models);
        }

        wv.availableFileList.setFileList(modelList);
        updateCacheFileDetails();
    }

    async updateInformationTagListMenu(cores = {}) {
        informationAnnotationFeature.availableInformationTags = this.getResourceListData("InformationTag", cores);
        await informationAnnotationFeature.populateAvailableAnnotationMenu();
    }

    update360ImageListMenu(cores = {}) {
        imageHotSpotFeature.available360ImageList = [];
        imageHotSpotFeature.setAvailable360Images(this.getResourceListData("360Image", cores));
    }

    update360VideoListMenu(cores = {}) {
        videoHotSpotFeature.available360VideoList = [];
        videoHotSpotFeature.setAvailable360Videos(this.getResourceListData("360Video", cores));
    }

    updateMediaPosterListMenu(cores = {}) {
        const imageList = this.getResourceListData("MediaScreen.POSTER", cores);

        if (!this.backupImageList && availableImageList && availableImageList.length > 0) {
            this.backupImageList = JSON.parse(JSON.stringify(availableImageList));
        }

        const sourceList = this.backupImageList || availableImageList;
        if (sourceList && sourceList.length > 0) {
            this.updateItemsRecursivelyById(imageList, sourceList);
        }

        if (this.backupImageList) {
            availableImageList.length = 0;
            availableImageList.push(...imageList);
        } else {
            availableImageList.splice(0, availableImageList.length);
            availableImageList.push(...imageList);
        }

        MediaScreenManager.updateAvailableListContainer(MediaScreenType.POSTER, availableImageList);
        ImageTreeController.refreshTree();
    }

    updateMediaVideoListMenu(cores = {}) {
        const videoList = this.getResourceListData("MediaScreen.VIDEO", cores);

        if (!this.backupVideoList && MediaScreenManager.availableVideoList && MediaScreenManager.availableVideoList.length > 0) {
            this.backupVideoList = JSON.parse(JSON.stringify(MediaScreenManager.availableVideoList));
        }

        const sourceList = this.backupVideoList || MediaScreenManager.availableVideoList;
        if (sourceList && sourceList.length > 0) {
            this.updateItemsRecursivelyById(videoList, sourceList);
        }

        if (this.backupVideoList) {
            MediaScreenManager.availableVideoList.length = 0;
            MediaScreenManager.availableVideoList.push(...videoList);
        } else {
            MediaScreenManager.availableVideoList.splice(0, MediaScreenManager.availableVideoList.length);
            MediaScreenManager.availableVideoList.push(...videoList);
        }

        MediaScreenManager.updateAvailableListContainer(MediaScreenType.VIDEO, MediaScreenManager.availableVideoList);
        VideoTreeController.refreshTree();
    }

    updateMediaPDFListMenu(cores = {}) {
        const pdfList = this.getResourceListData("MediaScreen.PDF", cores);

        if (!this.backupDocumentList && MediaScreenManager.availableDocumentList && MediaScreenManager.availableDocumentList.length > 0) {
            this.backupDocumentList = JSON.parse(JSON.stringify(MediaScreenManager.availableDocumentList));
        }

        const sourceList = this.backupDocumentList || MediaScreenManager.availableDocumentList;
        if (sourceList && sourceList.length > 0) {
            this.updateItemsRecursivelyById(pdfList, sourceList);
        }

        if (this.backupDocumentList) {
            MediaScreenManager.availableDocumentList.length = 0;
            MediaScreenManager.availableDocumentList.push(...pdfList);
        } else {
            MediaScreenManager.availableDocumentList.splice(0, MediaScreenManager.availableDocumentList.length);
            MediaScreenManager.availableDocumentList.push(...pdfList);
        }

        MediaScreenManager.updateAvailableListContainer(MediaScreenType.PDF, MediaScreenManager.availableDocumentList);
        DocumentTreeController.refreshTree();
    }

    async updateViewpointListData() {
        const updateViewpointListWithFetchedData = (items, fetchedDataMap) => {
            items.forEach(item => {
                if (!item.isFolder) {
                    const fetchedViewpoint = fetchedDataMap[item.id];
                    if (fetchedViewpoint) {
                        // Preserve hierarchy structure properties and only update viewpoint data
                        const structureProps = {
                            isFolder: item.isFolder,
                            children: item.children,
                            parent: item.parent,
                            id: item.id,
                            resourceId: item.resourceId,
                            _id: item._id
                        };
                        Object.assign(item, fetchedViewpoint, structureProps);
                    }
                } else if (item.children && item.children.length > 0) {
                    updateViewpointListWithFetchedData(item.children, fetchedDataMap);
                }
            });
        };

        const viewpointList = this.getResourceListData("Viewpoint");

        ViewPointManager.viewPointDataMap = {};

        const viewpointIds = this.getAvailableResourceIds(viewpointList);
        const fetchedViewpointData = {};

        for (const viewpointId of viewpointIds) {
            let viewpoint = null;
            const viewpointTrainingResource = viewpointList.find(vp => vp.id === viewpointId);
            if (viewpointTrainingResource.resourceId) {
                viewpoint = await viewpointService.getViewpointById({ id: viewpointId });
            } else {
                viewpoint = {
                    name: viewpointTrainingResource.name,
                    cameraPosition: {
                        x: viewpointTrainingResource.position[0],
                        y: viewpointTrainingResource.position[1],
                        z: viewpointTrainingResource.position[2]
                    },
                    cameraOrientation: {
                        x: viewpointTrainingResource.quaternion[0],
                        y: viewpointTrainingResource.quaternion[1],
                        z: viewpointTrainingResource.quaternion[2],
                        w: viewpointTrainingResource.quaternion[3]
                    }
                };
            }

            if (viewpoint) {
                fetchedViewpointData[viewpointId] = viewpoint;
            }
        }

        updateViewpointListWithFetchedData(viewpointList, fetchedViewpointData);
        
        // Only populate root-level items in ViewPointManager.viewPointDataMap
        // Child items should only exist within their folder's children array
        viewpointList.forEach(item => {
            ViewPointManager.viewPointDataMap[item.id] = item;
        });

        ViewPointManager.addToViewpointDataMap(viewpointList);
    }
}


export default new SceneMenuController();
